// ─── Predefined application themes ──────────────────────────────────────────
// Single source of truth for the theme presets applied app-wide. A preset's
// `mode` drives the MUI palette mode; its tokens drive the MUI palette + the
// semantic tokens returned by useThemeTokens() + the marketing token set, so
// the whole product (app, dashboard, and public marketing pages) shares one
// look.
//
// DEFAULT_THEME_ID is the committed product theme: 'slate-emerald'. The theme
// engine is retained (themeId in Redux, getTheme(preset)) so a different preset
// can be slotted in later, but there is no longer an in-app theme switcher.

export type ThemeMode = 'light' | 'dark';

export interface ThemeDef {
  id: string;
  name: string;
  mood: string;
  mode: ThemeMode;
  heading: 'serif' | 'sans';
  bg: string;          // app background
  surface: string;     // raised card / paper
  surfaceAlt: string;  // inset / stat tiles
  border: string;
  text: string;        // primary ink
  muted: string;       // secondary text
  primary: string;     // brand fill
  primaryDark: string; // brand hover / AA-safe text
  onPrimary: string;   // text on a primary fill
  accent: string;      // secondary jewel tone
}

export const DEFAULT_THEME_ID = 'buildersitenow-indigo';

export const THEMES: ThemeDef[] = [
  {
    id: 'obsidian-gold', name: 'Obsidian & Gold', mood: 'Dark luxe · jewellery-box', mode: 'dark', heading: 'serif',
    bg: '#0B0B0F', surface: '#16161C', surfaceAlt: '#1E1E26', border: '#34343E',
    text: '#F5F1E8', muted: '#AAA598', primary: '#C8A24B', primaryDark: '#A9863A', onPrimary: '#111111', accent: '#E0C47A',
  },
  {
    id: 'graphite-terracotta', name: 'Graphite & Terracotta', mood: 'Warm premium · current brand+', mode: 'light', heading: 'serif',
    bg: '#FAF7F2', surface: '#FFFDF9', surfaceAlt: '#F1EADE', border: '#DED5C4',
    text: '#1C1A17', muted: '#655C4F', primary: '#B5482E', primaryDark: '#8A2F1A', onPrimary: '#FFFCF6', accent: '#0F3A3A',
  },
  {
    id: 'midnight-bronze', name: 'Midnight & Bronze', mood: 'Modern SaaS · precise', mode: 'dark', heading: 'sans',
    bg: '#0C111B', surface: '#141B2A', surfaceAlt: '#1B2435', border: '#243049',
    text: '#EEF2F8', muted: '#97A2B6', primary: '#C8772E', primaryDark: '#A85F1E', onPrimary: '#0C111B', accent: '#5B8DEF',
  },
  {
    id: 'ivory-terracotta', name: 'Ivory & Terracotta', mood: 'Your live theme (baseline)', mode: 'light', heading: 'serif',
    bg: '#FAF7F2', surface: '#FFFDF9', surfaceAlt: '#F1EADE', border: '#DED5C4',
    text: '#1C1A17', muted: '#655C4F', primary: '#B5482E', primaryDark: '#8A2F1A', onPrimary: '#FFFCF6', accent: '#B5482E',
  },
  {
    id: 'slate-emerald', name: 'Slate & Emerald', mood: 'Clean modern · trustworthy', mode: 'light', heading: 'sans',
    bg: '#F8FAFC', surface: '#FFFFFF', surfaceAlt: '#F1F5F9', border: '#E2E8F0',
    text: '#0F172A', muted: '#64748B', primary: '#047857', primaryDark: '#065F46', onPrimary: '#FFFFFF', accent: '#0EA5E9',
  },
  {
    id: 'charcoal-amber', name: 'Charcoal & Amber', mood: 'Dark warm · industrial', mode: 'dark', heading: 'sans',
    bg: '#15140F', surface: '#201E18', surfaceAlt: '#2A2820', border: '#3A3729',
    text: '#F4EFE2', muted: '#B0A88F', primary: '#E0A427', primaryDark: '#BD871A', onPrimary: '#1A1709', accent: '#F2C14E',
  },
  {
    id: 'pearl-sapphire', name: 'Pearl & Sapphire', mood: 'Crisp corporate · fintech', mode: 'light', heading: 'sans',
    bg: '#F6F8FC', surface: '#FFFFFF', surfaceAlt: '#EEF2FA', border: '#DDE5F0',
    text: '#0B1B33', muted: '#5A6B85', primary: '#1E50C8', primaryDark: '#163C99', onPrimary: '#FFFFFF', accent: '#7C3AED',
  },
  {
    id: 'forest-cream', name: 'Forest & Cream', mood: 'Earthy premium · grounded', mode: 'light', heading: 'serif',
    bg: '#F7F4EC', surface: '#FFFEFA', surfaceAlt: '#EDE9DC', border: '#D8D2C0',
    text: '#1B2A20', muted: '#5C6B5F', primary: '#1F5C3D', primaryDark: '#15452C', onPrimary: '#FBFCF7', accent: '#C29A3B',
  },
  {
    id: 'plum-champagne', name: 'Plum & Champagne', mood: 'Dark opulent · boutique', mode: 'dark', heading: 'serif',
    bg: '#120B14', surface: '#1E1421', surfaceAlt: '#281A2C', border: '#3C2942',
    text: '#F3EAF2', muted: '#B09CB2', primary: '#C9A66B', primaryDark: '#A98A52', onPrimary: '#160E18', accent: '#B98ED6',
  },
  {
    id: 'sandstone-clay', name: 'Sandstone & Clay', mood: 'Soft minimal · approachable', mode: 'light', heading: 'sans',
    bg: '#FAF6F1', surface: '#FFFFFF', surfaceAlt: '#F2EBE2', border: '#E3D8C9',
    text: '#2B231C', muted: '#74675A', primary: '#B85C38', primaryDark: '#934527', onPrimary: '#FFF8F2', accent: '#3C6E71',
  },
  {
    id: 'carbon-electric', name: 'Carbon & Electric', mood: 'Dark techy · high-contrast', mode: 'dark', heading: 'sans',
    bg: '#0A0A0C', surface: '#141418', surfaceAlt: '#1C1C22', border: '#2C2C34',
    text: '#F2F3F5', muted: '#9A9CA4', primary: '#4F7CFF', primaryDark: '#3A62D8', onPrimary: '#FFFFFF', accent: '#22D3A8',
  },
  {
    id: 'linen-rust', name: 'Linen & Rust', mood: 'Editorial warm · understated', mode: 'light', heading: 'serif',
    bg: '#F7F3EE', surface: '#FFFFFF', surfaceAlt: '#EFE8DF', border: '#E0D6C8',
    text: '#221E1A', muted: '#6B6258', primary: '#A4471F', primaryDark: '#823717', onPrimary: '#FCF8F3', accent: '#2E4756',
  },

  // ─── Rich pack ──────────────────────────────────────────────────────────
  // Deeper, more saturated jewel-tone palettes. Brand fills are vivid; the
  // *Dark surfaces are kept near-black with a faint hue cast for richness.
  {
    id: 'emerald-noir', name: 'Emerald Noir', mood: 'Dark luxe · emerald & gold', mode: 'dark', heading: 'serif',
    bg: '#0A0F0C', surface: '#121B15', surfaceAlt: '#19271E', border: '#2A3B30',
    text: '#ECF3EE', muted: '#95A89C', primary: '#34C281', primaryDark: '#1FA869', onPrimary: '#07120C', accent: '#E2B24A',
  },
  {
    id: 'royal-sapphire', name: 'Royal Sapphire', mood: 'Dark regal · sapphire & cyan', mode: 'dark', heading: 'sans',
    bg: '#080C18', surface: '#10182C', surfaceAlt: '#172238', border: '#26314F',
    text: '#EAEEFB', muted: '#94A0C0', primary: '#3B82F6', primaryDark: '#2563EB', onPrimary: '#FFFFFF', accent: '#22D3EE',
  },
  {
    id: 'crimson-onyx', name: 'Crimson Onyx', mood: 'Dark opulent · crimson & gold', mode: 'dark', heading: 'serif',
    bg: '#100A0C', surface: '#1B1316', surfaceAlt: '#271A1E', border: '#3B2A2F',
    text: '#F5ECEE', muted: '#B59AA0', primary: '#E5484D', primaryDark: '#C13339', onPrimary: '#1A0A0C', accent: '#E8B04B',
  },
  {
    id: 'teal-graphite', name: 'Teal Graphite', mood: 'Dark fresh · teal & coral', mode: 'dark', heading: 'sans',
    bg: '#0B1012', surface: '#141B1E', surfaceAlt: '#1C262A', border: '#2B383D',
    text: '#ECF3F5', muted: '#93A6AC', primary: '#16C5B0', primaryDark: '#0E9C8C', onPrimary: '#04201D', accent: '#F2A65A',
  },
  {
    id: 'copper-charcoal', name: 'Copper Charcoal', mood: 'Dark warm · copper & jade', mode: 'dark', heading: 'serif',
    bg: '#0E0C0A', surface: '#181512', surfaceAlt: '#221D18', border: '#352D26',
    text: '#F4EFE9', muted: '#B2A597', primary: '#DA8546', primaryDark: '#BB6A30', onPrimary: '#1A0F08', accent: '#6FB1A6',
  },
  {
    id: 'emerald-ivory', name: 'Emerald Ivory', mood: 'Light premium · deep emerald & gold', mode: 'light', heading: 'serif',
    bg: '#FBFAF6', surface: '#FFFFFF', surfaceAlt: '#F1F2EC', border: '#E3E5DA',
    text: '#14201A', muted: '#5E6B62', primary: '#0B7A52', primaryDark: '#075F40', onPrimary: '#FBFDF9', accent: '#C99A2E',
  },
  {
    id: 'sapphire-frost', name: 'Sapphire Frost', mood: 'Light crisp · sapphire & teal', mode: 'light', heading: 'sans',
    bg: '#F6F9FD', surface: '#FFFFFF', surfaceAlt: '#EBF1FA', border: '#DCE6F2',
    text: '#0C1A2E', muted: '#56657E', primary: '#1457C8', primaryDark: '#0F4099', onPrimary: '#FFFFFF', accent: '#0EA5A0',
  },
  {
    id: 'saffron-teak', name: 'Saffron & Teak', mood: 'Light warm · saffron & deep teal', mode: 'light', heading: 'sans',
    bg: '#FBF6EE', surface: '#FFFFFF', surfaceAlt: '#F3EADC', border: '#E6D8C4',
    text: '#211A12', muted: '#6E6051', primary: '#D9781C', primaryDark: '#AC5D11', onPrimary: '#FFF8EF', accent: '#2E6E63',
  },
  {
    id: 'rose-bordeaux', name: 'Rose Bordeaux', mood: 'Light boutique · bordeaux & gold', mode: 'light', heading: 'serif',
    bg: '#FCF6F6', surface: '#FFFFFF', surfaceAlt: '#F6E9EA', border: '#ECD6D8',
    text: '#24151A', muted: '#6E5359', primary: '#B23150', primaryDark: '#8E2440', onPrimary: '#FFF6F7', accent: '#C68A2E',
  },
  {
    id: 'forest-honey', name: 'Forest & Honey', mood: 'Light grounded · forest & honey', mode: 'light', heading: 'serif',
    bg: '#F7F8F2', surface: '#FFFFFF', surfaceAlt: '#ECF0E3', border: '#DCE3CC',
    text: '#18221A', muted: '#5C6A5B', primary: '#2C7A4B', primaryDark: '#1E5C37', onPrimary: '#FAFCF7', accent: '#D7A22B',
  },

  // ─── shadcn/ui preview ────────────────────────────────────────────────────
  // Exact values from a generated shadcn/ui theme (cool-slate, oklch → hex).
  // White surfaces, hairline slate borders, near-black primary; the dark variant
  // flips to near-black surfaces with a near-white primary (shadcn's inverted
  // button). Semantic status hues (money-in green, overdue red) are NOT preset-
  // driven, so they stay coloured — neutral chrome + meaning-bearing colour.
  // Applied platform-wide by saving it as the backend theme in the Theme
  // Customizer (/admin/theme-customizer): picking the "shadcn Slate" preset
  // seeds this exact light + dark pair (see ThemeStudio.seedFromPreset), and
  // Save writes it to Settings.theme for every user + marketing. For a coloured
  // primary instead of monochrome, swap `primary`/`accent` to an emerald.
  {
    // Page canvas = muted gray (`--muted`), cards = pure white (`--card`): the
    // layered shadcn dashboard look (white panels floating on a gray canvas).
    id: 'shadcn-slate', name: 'shadcn Slate', mood: 'Neutral minimal · shadcn/ui', mode: 'light', heading: 'sans',
    bg: '#F1F3F3', surface: '#FFFFFF', surfaceAlt: '#F7F8F8', border: '#E3E7E8',
    // Primary text = black at 80% (#000000CC) — softer than a hard near-black.
    // Buttons/brand fills stay opaque (primary/accent below).
    text: '#000000CC', muted: '#67787C', primary: '#161B1D', primaryDark: '#090B0C', onPrimary: '#F9FBFB', accent: '#161B1D',
  },
  {
    id: 'shadcn-slate-dark', name: 'shadcn Slate (dark)', mood: 'Neutral minimal · shadcn/ui', mode: 'dark', heading: 'sans',
    bg: '#090B0C', surface: '#161B1D', surfaceAlt: '#22292B', border: '#232426',
    text: '#F9FBFB', muted: '#9CA8AB', primary: '#E3E7E8', primaryDark: '#FFFFFF', onPrimary: '#161B1D', accent: '#E3E7E8',
  },
  {
    // BuilderSiteNow brand palette — mapped from the design tokens / colors PDF.
    // Page=Primary-50, Card=Neutral-50, border=Neutral-100, ink=Neutral-900/500,
    // brand button=Primary-500 (#3C4E90) hover Primary-600, on-button=Primary-50.
    id: 'buildersitenow-indigo', name: 'BuilderSiteNow Indigo', mood: 'Brand palette · clean SaaS', mode: 'light', heading: 'sans',
    bg: '#F3F4F7', surface: '#FAFAFA', surfaceAlt: '#EBEDF3', border: '#DEDEE1',
    text: '#121318', muted: '#585A67', primary: '#3C4E90', primaryDark: '#283460', onPrimary: '#F3F4F7', accent: '#5468C0',
  },
  {
    // Dark pairing of the brand palette (Primary-900/800/700 chrome).
    id: 'buildersitenow-indigo-dark', name: 'BuilderSiteNow Indigo (dark)', mood: 'Brand palette · clean SaaS', mode: 'dark', heading: 'sans',
    bg: '#0A0D18', surface: '#141A30', surfaceAlt: '#1E2748', border: '#2A3352',
    text: '#EEF0F6', muted: '#9EA7C8', primary: '#4E62B4', primaryDark: '#3C4E90', onPrimary: '#F3F4F7', accent: '#7D89B5',
  },
];

/** Resolve a preset by id, falling back to the default theme. Never null. */
export function getThemeById(id: string | undefined | null): ThemeDef {
  return THEMES.find((t) => t.id === id) ?? THEMES.find((t) => t.id === DEFAULT_THEME_ID)!;
}
